import React, { useState, useEffect } from 'react';
import {
    X, CheckCircle, Clock, XCircle, Search, ChevronLeft, ChevronRight,
    ChevronUp, ChevronDown, RefreshCw, Building2, FileText,
    CreditCard, Package, Shield, AlertCircle, Calendar,
    Hash, Stethoscope,
} from 'lucide-react';
import { apiService } from '../../api/api';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const PRACTICE_TYPES = { 1: 'General Practice', 2: 'Specialty Clinic', 3: 'Hospital', 4: 'Urgent Care' };
const getPracticeType = (id) => PRACTICE_TYPES[parseInt(id)] ?? `Type ${id}`;

const LS_KEY = 'admin_approvedClinicIds';

const getLocalApproved = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); }
    catch { return []; }
};

const saveLocalApproved = (ids) => {
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
};

// ─────────────────────────────────────────────────────────────
// StatusBadge
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ isApproved }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
        background: isApproved ? '#dcfce7' : '#fef9c3',
        color: isApproved ? '#166534' : '#854d0e',
        border: `1px solid ${isApproved ? '#bbf7d0' : '#fde68a'}`,
    }}>
        <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: isApproved ? '#16a34a' : '#f59e0b',
            display: 'inline-block',
        }} />
        {isApproved ? 'Approved' : 'Pending'}
    </span>
);

// ─────────────────────────────────────────────────────────────
// SectionTitle
// ─────────────────────────────────────────────────────────────
const SectionTitle = ({ icon: Icon, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={14} color="#018d71" />
        <span style={{ fontSize: 11, fontWeight: 800, color: '#018d71', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
    </div>
);

// ─────────────────────────────────────────────────────────────
// InfoRow
// ─────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, highlight }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={15} color="#4361ee" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: highlight ? 700 : 500, color: highlight ? '#1a202c' : '#374151', wordBreak: 'break-word' }}>
                {value ?? <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>N/A</span>}
            </div>
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────
// LicenseCard
// ─────────────────────────────────────────────────────────────
const LicenseCard = ({ lic }) => (
    <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4361ee' }}>License #{lic.clinicLicenseId}</span>
            <span style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 8 }}>
                Type ID: {lic.licenseTypeId}
            </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            {[
                { label: 'License No.', value: lic.licenseNumber },
                { label: 'Authorized Sign', value: lic.authorizedSign },
                { label: 'Expiry Date', value: lic.expiryDate ? new Date(lic.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                { label: 'Created At', value: lic.createdAt ? new Date(lic.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
            ].map(({ label, value }) => (
                <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{value ?? <span style={{ color: '#d1d5db' }}>—</span>}</div>
                </div>
            ))}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────
// Clinic Detail Modal
// ─────────────────────────────────────────────────────────────
const ClinicDetailModal = ({ clinic, onClose, onApprove }) => {
    const [approving, setApproving] = useState(false);
    const [approveError, setApproveError] = useState(null);

    if (!clinic) return null;

    // ✅ No local state — read directly from prop so parent controls truth
    const approved = clinic.isApproved === true;

    const handleApprove = async () => {
        setApproving(true);
        setApproveError(null);
        try {
            const clinicId = clinic.clinicId || clinic.ClinicId;
            await apiService.put(`/Clinic/approve/${clinicId}`, {});
            onApprove && onApprove(clinicId); // parent updates state + localStorage
        } catch (err) {
            setApproveError(err?.response?.data?.message || err.message || 'Approval failed.');
        } finally {
            setApproving(false);
        }
    };

    const createdFmt = clinic.createdDate
        ? new Date(clinic.createdDate).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        })
        : null;

    const licenses = Array.isArray(clinic.clinicLicenses) ? clinic.clinicLicenses : [];

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(3px)' }}
            onClick={onClose}
        >
            <div
                style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 28px 72px rgba(0,0,0,0.22)' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ background: 'linear-gradient(135deg,#018d71,#016a55)', padding: '22px 24px', borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, zIndex: 2 }}>
                    <div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: 19, marginBottom: 4 }}>{clinic.clinicName || 'Clinic Details'}</div>
                        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>Clinic ID: {clinic.clinicId} &nbsp;·&nbsp; User ID: {clinic.userId}</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', flexShrink: 0 }}>
                        <X size={16} />
                    </button>
                </div>

                {/* Status strip */}
                <div style={{ padding: '14px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</span>
                    <StatusBadge isApproved={approved} />
                    {clinic.status && (
                        <span style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>Active</span>
                    )}
                </div>

                <div style={{ padding: '0 24px 24px' }}>

                    {/* Clinic Info */}
                    <div style={{ marginTop: 20 }}>
                        <SectionTitle icon={Building2} label="Clinic Information" />
                        <InfoRow icon={Building2} label="Clinic Name" value={clinic.clinicName} highlight />
                        <InfoRow icon={Stethoscope} label="Practice Type" value={getPracticeType(clinic.practiceType)} />
                        <InfoRow icon={Hash} label="NPI Number" value={clinic.npi} />
                        <InfoRow icon={Calendar} label="Created Date" value={createdFmt} />
                        <InfoRow icon={Package} label="Preferred Order Method" value={clinic.preferredOrderMethod} />
                    </div>

                    {/* Financial */}
                    <div style={{ marginTop: 20 }}>
                        <SectionTitle icon={CreditCard} label="Financial Details" />
                        <InfoRow icon={CreditCard} label="Payment Method" value={clinic.paymentMethod} />
                        <InfoRow icon={Package} label="Monthly Spend" value={clinic.monthlySpend ? `$${clinic.monthlySpend}` : null} />
                    </div>

                    {/* Compliance */}
                    <div style={{ marginTop: 20 }}>
                        <SectionTitle icon={Shield} label="Compliance" />
                        <InfoRow
                            icon={Shield}
                            label="Valid Medical License"
                            value={
                                <span style={{ color: clinic.hasValidMedicalLicense ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                    {clinic.hasValidMedicalLicense ? '✓ Yes' : '✗ No'}
                                </span>
                            }
                        />
                    </div>

                    {/* Notes */}
                    {clinic.notes && clinic.notes.trim() && (
                        <div style={{ marginTop: 20 }}>
                            <SectionTitle icon={FileText} label="Notes" />
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 14, color: '#92400e', lineHeight: 1.6 }}>
                                {clinic.notes}
                            </div>
                        </div>
                    )}

                    {/* Licenses */}
                    {licenses.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <SectionTitle icon={FileText} label={`Clinic Licenses (${licenses.length})`} />
                            {licenses.map((lic, i) => <LicenseCard key={lic.clinicLicenseId ?? i} lic={lic} />)}
                        </div>
                    )}

                    {/* Approve Section */}
                    <div style={{ marginTop: 24, padding: '18px', background: approved ? '#f0fdf4' : '#f8fafc', borderRadius: 12, border: `1px solid ${approved ? '#bbf7d0' : '#e2e8f0'}` }}>
                        {approved ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#16a34a' }}>
                                <CheckCircle size={20} />
                                <span style={{ fontWeight: 700, fontSize: 15 }}>This clinic has been approved</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                                    Review the clinic details above before approving. This action will mark the clinic as approved.
                                </div>
                                {approveError && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', background: '#fee2e2', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                                        <AlertCircle size={15} /> {approveError}
                                    </div>
                                )}
                                <button
                                    onClick={handleApprove}
                                    disabled={approving}
                                    style={{
                                        width: '100%', padding: '13px',
                                        background: approving ? '#6b7280' : '#018d71',
                                        color: '#fff', border: 'none', borderRadius: 10,
                                        fontSize: 15, fontWeight: 700,
                                        cursor: approving ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={e => { if (!approving) e.currentTarget.style.background = '#016a55'; }}
                                    onMouseLeave={e => { if (!approving) e.currentTarget.style.background = '#018d71'; }}
                                >
                                    {approving ? (
                                        <>
                                            <div style={{ width: 18, height: 18, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            Approving...
                                        </>
                                    ) : (
                                        <><CheckCircle size={18} /> Approve Clinic</>
                                    )}
                                </button>
                            </>
                        )}
                    </div>

                </div>
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN — ClinicPage
// ─────────────────────────────────────────────────────────────
const ClinicPage = () => {
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterApproved, setFilterApproved] = useState('all');
    const [sortField, setSortField] = useState('clinicId');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedClinic, setSelectedClinic] = useState(null);

    useEffect(() => { fetchClinics(); }, []);
    const fetchClinics = async () => {
        setLoading(true); setError(null);
        try {
            const res = await apiService.get('/Clinic');
            const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

            const localApproved = getLocalApproved();

            const merged = arr.map(c => {
                const backendApproved = c.isApproved === true;
                const locallyApproved = localApproved.includes(c.clinicId);

                // If backend now returns approved, save it to localStorage too
                if (backendApproved && !locallyApproved) {
                    localApproved.push(c.clinicId);
                }

                return {
                    ...c,
                    isApproved: backendApproved || locallyApproved,
                };
            });

            // Persist any backend-confirmed approvals into localStorage
            saveLocalApproved([...new Set(localApproved)]);

            setClinics(merged);
        } catch (err) {
            setError('Failed to load clinics. ' + (err?.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // ✅ Called when approve API succeeds
    const handleApproved = (clinicId) => {
        // 1. Persist to localStorage
        const prev = getLocalApproved();
        const updated = [...new Set([...prev, clinicId])];
        saveLocalApproved(updated);

        // 2. Update clinics list → table row flips to Approved
        setClinics(list => list.map(c =>
            c.clinicId === clinicId ? { ...c, isApproved: true } : c
        ));

        // 3. Update open modal → badge flips immediately
        setSelectedClinic(prev => prev ? { ...prev, isApproved: true } : prev);
    };

    const stats = {
        total: clinics.length,
        approved: clinics.filter(c => c.isApproved === true).length,
        pending: clinics.filter(c => c.isApproved !== true).length,
    };

    const processed = React.useMemo(() => {
        let res = [...clinics];

        if (filterApproved === 'approved') res = res.filter(c => c.isApproved === true);
        if (filterApproved === 'pending') res = res.filter(c => c.isApproved !== true);

        if (searchText.trim()) {
            const q = searchText.trim().toLowerCase();
            res = res.filter(c =>
                Object.values(c).some(v =>
                    v !== null && v !== undefined && String(v).toLowerCase().includes(q)
                )
            );
        }

        res.sort((a, b) => {
            let av = a[sortField], bv = b[sortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return res;
    }, [clinics, filterApproved, searchText, sortField, sortDir]);

    useEffect(() => { setPage(1); }, [filterApproved, searchText, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
    const paginated = processed.slice((page - 1) * pageSize, page * pageSize);

    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ChevronUp size={12} style={{ color: '#d1d5db' }} />;
        return sortDir === 'asc'
            ? <ChevronUp size={12} style={{ color: '#4361ee' }} />
            : <ChevronDown size={12} style={{ color: '#4361ee' }} />;
    };

    // ── Loading ──
    if (loading) return (
        <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                <div style={{ display: 'inline-block', width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: '#018d71', borderRadius: '50%', animation: 'spin 0.9s linear infinite', marginBottom: 18 }} />
                <div style={{ fontSize: 16, color: '#718096', fontWeight: 500 }}>Loading clinics...</div>
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );

    // ── Error ──
    if (error) return (
        <div className="admin-content">
            <div style={{ textAlign: 'center', padding: 48, background: '#fee2e2', borderRadius: 12, border: '1px solid #fecaca', maxWidth: 480, margin: '40px auto' }}>
                <XCircle size={48} color="#dc2626" style={{ marginBottom: 12 }} />
                <h3 style={{ color: '#dc2626', marginBottom: 8 }}>Failed to Load Clinics</h3>
                <p style={{ color: '#991b1b', marginBottom: 20, fontSize: 14 }}>{error}</p>
                <button onClick={fetchClinics} style={{ padding: '9px 22px', background: '#018d71', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <RefreshCw size={15} /> Retry
                </button>
            </div>
        </div>
    );

    // ── Main ──
    return (
        <div className="admin-content">

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a202c' }}>Clinics</h1>
                    <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>Manage and approve clinic registrations</p>
                </div>
            </div>

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 28 }}>
                {[
                    { label: 'Total Clinics', value: stats.total, color: '#4361ee', bg: '#eff6ff', border: '#bfdbfe', icon: <Building2 size={20} /> },
                    { label: 'Approved', value: stats.approved, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircle size={20} /> },
                    { label: 'Pending', value: stats.pending, color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: <Clock size={20} /> },
                ].map(s => (
                    <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}`, display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 10, background: s.bg, border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, flexShrink: 0 }}>
                            {s.icon}
                        </div>
                        <div>
                            <div style={{ fontSize: 26, fontWeight: 800, color: '#1a202c', lineHeight: 1 }}>{s.value}</div>
                            <div style={{ fontSize: 12, color: '#718096', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: 'All', count: stats.total, activeColor: '#356febe1' },
                        { key: 'approved', label: 'Approved', count: stats.approved, activeColor: '#16a34a88' },
                        { key: 'pending', label: 'Pending', count: stats.pending, activeColor: '#ffcf7dcc' },
                    ].map(tab => {
                        const active = filterApproved === tab.key;
                        return (
                            <button key={tab.key} onClick={() => setFilterApproved(tab.key)} style={{
                                padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                background: active ? tab.activeColor : '#f8fafc',
                                color: active ? '#030303' : '#4a5568',
                                border: active ? `2px solid ${tab.activeColor}` : '2px solid #e2e8f0',
                                transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 7,
                            }}>
                                {tab.label}
                                <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 11, background: active ? 'rgba(255,255,255,0.25)' : '#e2e8f0', color: active ? '#000' : '#718096' }}>
                                    {tab.count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Search */}
                <div style={{ position: 'relative', width: 280 }}>
                    <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                        type="text"
                        placeholder="Search clinics…"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: '100%', padding: '9px 32px 9px 34px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#1a202c' }}
                        onFocus={e => (e.target.style.borderColor = '#018d71')}
                        onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                    />
                    {searchText && (
                        <button onClick={() => setSearchText('')} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex' }}>
                            <X size={13} />
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #f0f0f0' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#718096' }}>
                        Showing <strong style={{ color: '#1a202c' }}>{Math.min((page - 1) * pageSize + 1, processed.length)}–{Math.min(page * pageSize, processed.length)}</strong> of <strong style={{ color: '#1a202c' }}>{processed.length}</strong> clinics
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: '#718096' }}>Per page:</span>
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            style={{ padding: '5px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#fff', color: '#374151' }}>
                            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>

                {processed.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <Building2 size={50} color="#cbd5e0" style={{ marginBottom: 12 }} />
                        <h3 style={{ color: '#4a5568', margin: '0 0 6px', fontSize: 17 }}>No Clinics Found</h3>
                        <p style={{ color: '#718096', fontSize: 14, margin: 0 }}>No clinics match your search.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    {[
                                        { label: 'Clinic ID', field: 'clinicId' },
                                        { label: 'Clinic Name', field: 'clinicName' },
                                        { label: 'Practice', field: 'practiceType' },
                                        { label: 'NPI', field: 'npi' },
                                        { label: 'Monthly Spend', field: 'monthlySpend' },
                                        { label: 'Status', field: 'isApproved' },
                                        { label: 'Action', field: null },
                                    ].map(col => (
                                        <th key={col.label}
                                            onClick={() => col.field && toggleSort(col.field)}
                                            style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: col.field ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                {col.label}
                                                {col.field && <SortIcon field={col.field} />}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((clinic, idx) => (
                                    <tr key={clinic.clinicId ?? idx}
                                        style={{ borderBottom: '1px solid #f7f7f7', background: '#fff', transition: 'background 0.1s' }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#fafbff')}
                                        onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    >
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontWeight: 700, color: '#4361ee', fontSize: 13 }}>#{clinic.clinicId}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a202c' }}>{clinic.clinicName}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontSize: 13, color: '#374151' }}>{getPracticeType(clinic.practiceType)}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontSize: 13, color: '#6b7280', fontFamily: 'monospace' }}>{clinic.npi || '—'}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontSize: 13, color: '#374151' }}>{clinic.monthlySpend ? `$${clinic.monthlySpend}` : '—'}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <StatusBadge isApproved={clinic.isApproved === true} />
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            {clinic.isApproved === true ? (
                                                // Approved — View only
                                                <button
                                                    onClick={() => setSelectedClinic(clinic)}
                                                    style={{
                                                        padding: '6px 18px', background: '#f0fdf4', color: '#16a34a',
                                                        border: '1.5px solid #bbf7d0', borderRadius: 7, fontSize: 13,
                                                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                                                >
                                                    <CheckCircle size={13} /> View
                                                </button>
                                            ) : (
                                                // Pending — View & Review for approval
                                                <button
                                                    onClick={() => setSelectedClinic(clinic)}
                                                    style={{
                                                        padding: '6px 18px', background: '#fff', color: '#018D71',
                                                        border: '1.5px solid #bfdbfe', borderRadius: 7, fontSize: 13,
                                                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = '#018D71'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#018D71'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#018D71'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                                                >
                                                    Review
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px 20px', gap: 6, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                            style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: page === 1 ? '#f7fafc' : '#fff', color: page === 1 ? '#cbd5e0' : '#4a5568', cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500 }}>
                            <ChevronLeft size={15} /> Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                            .map((p, i) =>
                                p === '...'
                                    ? <span key={`e${i}`} style={{ padding: '7px 4px', color: '#9ca3af', fontSize: 13 }}>···</span>
                                    : <button key={p} onClick={() => setPage(p)}
                                        style={{ width: 36, height: 36, border: '1.5px solid', borderRadius: 7, fontWeight: page === p ? 700 : 500, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', borderColor: page === p ? '#4361ee' : '#e2e8f0', background: page === p ? '#4361ee' : '#fff', color: page === p ? '#fff' : '#4a5568' }}>
                                        {p}
                                    </button>
                            )
                        }
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                            style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: page === totalPages ? '#f7fafc' : '#fff', color: page === totalPages ? '#cbd5e0' : '#4a5568', cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500 }}>
                            Next <ChevronRight size={15} />
                        </button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <ClinicDetailModal
                clinic={selectedClinic}
                onClose={() => setSelectedClinic(null)}
                onApprove={handleApproved}
            />

            <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          div[style*="repeat(3,1fr)"] { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 500px) {
          div[style*="repeat(3,1fr)"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
        </div>
    );
};

export default ClinicPage;